'use client';

import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useStoreApi } from '@/app/providers/store';
import { hasLiveFaceConsent, saveLiveFaceConsent } from '../_hooks/live-call-consent';
import { LiveFaceConsentModal } from './LiveFaceConsentModal';

interface UseLiveFaceConsentResult {
  /** Runs `action` directly when face consent exists (or off the China site), otherwise asks for consent first. */
  runWithFaceConsent: (action: () => void | Promise<void>) => void;
  /** Face consent dialog; callers mount it in their own JSX. */
  faceConsentModal: ReactNode;
}

interface UseLiveFaceConsentOptions {
  /** 已由小程序在当前用户维度持久化确认的面部信息处理授权。 */
  hasMiniappFaceConsent?: boolean;
  /** 小程序 WebView 必须以小程序传入的用户级授权状态为准。 */
  isMiniappWebView?: boolean;
}

const notifyMiniappFaceConsentConfirmed = () => {
  window.wx?.miniProgram?.postMessage?.({
    data: { type: 'live_face_consent_confirmed' },
  });
};

/** Guards camera and portrait upload actions behind the face recognition consent. */
export function useLiveFaceConsent({
  hasMiniappFaceConsent = false,
  isMiniappWebView = false,
}: UseLiveFaceConsentOptions = {}): UseLiveFaceConsentResult {
  const storeApi = useStoreApi();
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmedInCurrentPage, setIsConfirmedInCurrentPage] = useState(false);
  const pendingActionRef = useRef<(() => void | Promise<void>) | null>(null);

  const runWithFaceConsent = useCallback(
    (action: () => void | Promise<void>) => {
      // 小程序 WebView 每次打开都是新页面，且其授权真源在小程序：不能因
      // Matrix H5 残留的 localStorage 误判为已授权。普通 H5 仍沿用原有存储。
      const hasConsent =
        hasMiniappFaceConsent ||
        isConfirmedInCurrentPage ||
        (!isMiniappWebView && hasLiveFaceConsent(storeApi.getState().userInfo?.id));
      if (hasConsent) {
        void action();
        return;
      }
      pendingActionRef.current = action;
      setIsOpen(true);
    },
    [hasMiniappFaceConsent, isConfirmedInCurrentPage, isMiniappWebView, storeApi],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      pendingActionRef.current = null;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    const userId = storeApi.getState().userInfo?.id;
    if (userId) {
      saveLiveFaceConsent(userId);
    }
    // 微信 WebView 的 H5 存储不会替代小程序侧的用户级授权记录；
    // 回传确认事件，由小程序在 onMessage 中持久化。
    notifyMiniappFaceConsentConfirmed();
    setIsConfirmedInCurrentPage(true);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action) {
      void action();
    }
  }, [storeApi]);

  const faceConsentModal = (
    <LiveFaceConsentModal open={isOpen} onOpenChange={handleOpenChange} onConfirm={handleConfirm} />
  );

  return { runWithFaceConsent, faceConsentModal };
}
